/* =====================================================================
   SUSU - personalisation engine
   ---------------------------------------------------------------------
   Shows/hides each personalisation field based on the selected variant
   option, and draws the customer's typed text onto the garment image
   live as they type.

   Replaces the two scripts that used to live inline in theme.liquid
   (the "Custom Back Text" show/hide, and the back-print preview).

   ADDING A PRINT POSITION is one entry in ZONES below. See
   docs/personalisation.md for the full procedure.

   Print geometry comes from the ALT TEXT of each garment image, e.g.

     Navy hoodie, back view [[bp for="Navy" x=50 y=38 w=46 ink=#ffffff]]

   Add ?bpdebug=1 to a product URL to outline every print box.
   Add ?bpdebug=front to outline only one zone.
   ===================================================================== */
(() => {
  'use strict';

  /* ============================ CONFIG ============================ */

  /* The variant option(s) that decide which positions are being bought.
     'Back print' is the legacy name and is kept so existing products keep
     working untouched. */
  const OPTION_NAMES = ['Personalisation', 'Back print'];

  /* Whole option values meaning "no personalisation at all". */
  const OFF_VALUES = ['blank', 'none', 'no print', 'no', 'plain'];

  /* One entry per print position.
       id        internal name, and the ?bpdebug= value
       marker    the alt-text tag: [[bp ...]], [[fp ...]]
       property  the line-item property, i.e. properties[Custom Back Text]
       match     tokens that switch this zone on, matched as whole words
                 against each segment of the selected option value       */
  const ZONES = [
    {
      id: 'back',
      marker: 'bp',
      property: 'Custom Back Text',
      match: ['back', 'custom text'] // 'custom text' = legacy Back print products
    },
    {
      id: 'front',
      marker: 'fp',
      property: 'Custom Front Text',
      match: ['front']
    },
    {
      id: 'left sleeve',
      marker: 'lsp',
      property:
      'Custom Left Sleeve Text',
      match: ['left sleeve']
    }
    
  ];

  const COLOUR_OPTIONS = ['Colour', 'Color']; // option holding the garment colour
  const STRIP_ALT = true;  // remove the [[..]] tag from alt so screen readers skip it
  const SPLIT_ON_PIPE = true;  // treat a typed "|" as a line break
  const MAX_LINES = 4;
  const MIN_FONT_PX = 5;

  /* Per-image defaults, all overridable in the alt-text tag. */
  const DEFAULTS = {
    for: null,      // colour(s) this image is for. Omit = any colour.
    x: 50,          // print-box centre, % of image width
    y: 40,          // print-box centre, % of image height
    w: 46,          // print-box width, % of image width
    h: 22,          // print-box height, % of image height
    size: 9,        // max font size, % of image height
    ink: '#ffffff', // text colour
    font: null,     // font-family override; default = theme heading font
    weight: 700,
    spacing: 0.02,  // letter-spacing, em
    lh: 1.08,       // line height
    rotate: 0,      // degrees
    case: 'none',   // none | upper | lower
    shadow: 0,      // 0-1 drop shadow strength, for light garments
    opacity: 1
  };

  const NUMERIC = ['x', 'y', 'w', 'h', 'size', 'spacing', 'lh', 'rotate', 'shadow', 'opacity', 'weight'];
  const KV_RE = /([a-z_]+)\s*=\s*("[^"]*"|'[^']*'|[^\s\]]+)/gi;

  /* ?bpdebug=1 outlines every zone; ?bpdebug=front outlines only that zone. */
  const DEBUG_RAW = (/[?&]bpdebug=([^&]*)/.exec(location.search) || [])[1] || '';
  const DEBUG_ON = DEBUG_RAW !== '';
  const DEBUG_ZONE = DEBUG_RAW && DEBUG_RAW !== '1' ? DEBUG_RAW.toLowerCase() : null;

  /* ================ list mode: per-product print zones ================
     blocks/susu-personalisation-list.liquid renders, per product,

       <script type="application/json" data-susu-zones>
         [{"id":"right chest","marker":"rc","property":"Right Chest Initials"}]
       </script>

     listing ONLY the fields that declare a preview marker. A field with no
     marker must never reach ZONES: susu-variant-media-filter.js builds its
     regex as an alternation of these markers, and an empty alternative
     matches at offset 0 of every string, so every image in the store would
     look tagged and start being colour-filtered.

     MERGED HERE, at top level, deliberately. Both scripts are `defer`, so
     the whole document is parsed before either runs, and the media filter
     reads window.SUSU_ZONES at ITS top level. A merge inside start() waits
     for DOMContentLoaded and would land after that read, doing nothing.

     See docs/personalisation-list.md. */
  function mergeListZones() {
    document
      .querySelectorAll('script[type="application/json"][data-susu-zones]')
      .forEach((node) => {
        let declared;
        try { declared = JSON.parse(node.textContent || '[]'); } catch (e) { return; }
        if (!Array.isArray(declared)) return;

        declared.forEach((z) => {
          if (!z || typeof z !== 'object') return;

          const marker = String(z.marker == null ? '' : z.marker).trim();
          const property = String(z.property == null ? '' : z.property).trim();
          if (!marker || !property) return;   // no marker = capture-only field

          /* A marker is an alt-text tag name and an overlay key, so it has to
             be unique; two controls on one line-item property is a data
             problem Shopify resolves by keeping one of them. Skip either. */
          if (ZONES.some((e) => e.marker === marker)) {
            if (DEBUG_ON) console.warn('[susu] marker already in use, list zone skipped:', marker);
            return;
          }
          if (ZONES.some((e) => e.property === property)) {
            if (DEBUG_ON) console.warn('[susu] property already in use, list zone skipped:', property);
            return;
          }

          ZONES.push({
            id: String(z.id || marker).trim().toLowerCase(),
            marker: marker,
            property: property,
            match: [],      // never matched against an option value
            listMode: true  // the flag zoneActive / updateFieldVisibility key on
          });
        });
      });
  }

  mergeListZones();

  /* Let the variant media filter discover our markers rather than
     hardcoding them a second time. */
  window.SUSU_ZONES = ZONES;

  /* ============================ STATE ============================ */

  const overlays = new Map();   // "marker\u0000uid" -> { img, ov }
  let headingFontCache = null;
  let scheduled = false;
  let ro = null;
  let uidSeq = 0;

  /* ========================= small helpers ========================= */

  const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

  const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  function imgUid(img) {
    if (!img.dataset.susuUid) img.dataset.susuUid = String(++uidSeq);
    return img.dataset.susuUid;
  }

  function markerRe(marker) {
    return new RegExp('\\[\\[\\s*' + escapeRe(marker) + '\\b([^\\]]*)\\]\\]', 'i');
  }

  function scheduleUpdate() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; update(); });
  }

  function headingFont(scope) {
    if (headingFontCache) return headingFontCache;
    const rootStyle = getComputedStyle(document.documentElement);
    const vars = [
      '--font-heading--family', '--font-heading-family',
      '--heading-font-family', '--font-heading', '--font-h1--family'
    ];
    for (const v of vars) {
      const val = rootStyle.getPropertyValue(v).trim();
      if (val) return (headingFontCache = val);
    }
    const h = (scope || document).querySelector('h1, .h1, h2');
    headingFontCache = h ? getComputedStyle(h).fontFamily : 'inherit';
    return headingFontCache;
  }

  /* ====================== option-value gating ====================== */

  /* Split "Back + Front" into ["back", "front"]. */
  function segmentsOf(value) {
    return String(value == null ? '' : value)
      .split(/[+,/&]|\band\b/i)
      .map(norm)
      .filter(Boolean);
  }

  /* Is this zone switched on by the selected option value?
     Returns null when the product has no such option at all, meaning
     "no opinion" - the field then speaks for itself. */
  function zoneActive(zone, optionValue) {
    /* A list-mode zone is not sold as an option value, so no option value can
       switch it off. `null` is the answer the engine already gives when the
       product has no Personalisation option; this makes it the answer on a
       MIXED product too, where the option exists but says nothing about this
       field. Without it, `match: []` evaluates false and the preview dies. */
    if (zone.listMode) return null;
    if (optionValue === null || optionValue === undefined) return null;
    const whole = norm(optionValue);
    if (!whole) return false;
    if (OFF_VALUES.indexOf(whole) !== -1) return false;

    return segmentsOf(optionValue).some((seg) => {
      /* "no back print" must not switch the back zone on. */
      if (/^(no|without|not)\b/.test(seg)) return false;
      return zone.match.some((tok) => {
        const t = norm(tok);
        return seg === t || new RegExp('\\b' + escapeRe(t) + '\\b').test(seg);
      });
    });
  }

  function selectedOptionValue(picker, names) {
    if (!picker) return null;
    const wanted = names.map(norm);
    const nodes = picker.querySelectorAll('[data-option-name]');
    for (const node of nodes) {
      if (wanted.indexOf(norm(node.dataset.optionName)) === -1) continue;
      if (node instanceof HTMLInputElement && node.checked) return node.value;
      if (node instanceof HTMLOptionElement && node.selected) return node.value;
      if (node instanceof HTMLSelectElement) return node.value;
    }
    return null;
  }

  /* ========================= alt-text config ========================= */

  function parseConfig(raw, marker) {
    const m = markerRe(marker).exec(raw || '');
    if (!m) return null;
    const cfg = Object.assign({}, DEFAULTS);
    let kv;
    KV_RE.lastIndex = 0;
    while ((kv = KV_RE.exec(m[1])) !== null) {
      const key = kv[1].toLowerCase();
      const val = kv[2].replace(/^['"]|['"]$/g, '');
      if (!(key in DEFAULTS)) continue;
      if (NUMERIC.indexOf(key) !== -1) {
        const n = parseFloat(val);
        if (!isFinite(n)) continue;
        cfg[key] = n;
      } else {
        cfg[key] = val;
      }
    }
    cfg.w = Math.max(1, Math.min(100, cfg.w));
    cfg.h = Math.max(1, Math.min(100, cfg.h));
    return cfg;
  }

  /* Reads config once per image PER ZONE, caches it on the element, then
     cleans that zone's marker out of the visible alt text. A miss is cached
     as "0" so we don't re-run one regex per zone over every image every
     frame. */
  function configFor(img, marker) {
    const attr = 'data-susu-cfg-' + marker;
    const cached = img.getAttribute(attr);
    if (cached === '0') return null;
    if (cached) {
      try { return JSON.parse(cached); } catch (e) { /* fall through, re-parse */ }
    }

    const cfg = parseConfig(img.getAttribute('alt'), marker);
    if (!cfg) { img.setAttribute(attr, '0'); return null; }

    img.setAttribute(attr, JSON.stringify(cfg));
    if (STRIP_ALT) {
      const original = String(img.getAttribute('alt') || '');
      /* Keep the alt as written, before any tag is stripped. parseConfig
         keeps only DEFAULTS keys, so choice keys like logo="Crest" -- which
         susu-variant-media-filter.js reads to switch the image with a
         personalisation dropdown -- would otherwise be gone by the time the
         filter looks, if this engine got to the image first. Set once, so the
         second zone's strip does not overwrite it with a half-stripped alt. */
      if (!('susuAltRaw' in img.dataset)) img.dataset.susuAltRaw = original;
      const clean = original
        .replace(markerRe(marker), '').replace(/\s{2,}/g, ' ').trim();
      /* Remember exactly what we wrote. Stripping the tag mutates alt, which
         trips our own MutationObserver - without this it would invalidate the
         config we just cached, and the re-parse would then find no tag. */
      img.dataset.susuAltClean = clean;
      img.setAttribute('alt', clean);
    }
    return cfg;
  }

  /* The wording of an alt with every [[..]] tag taken out, spaced as the
     variant media filter leaves it. */
  function altWords(alt) {
    return String(alt == null ? '' : alt)
      .replace(/\[\[[^\]]*\]\]/g, '')
      .replace(/\s{2,}/g, ' ').replace(/\s+([,.])/g, '$1').trim();
  }

  /* Was this alt change only another script stripping ITS tag? The variant
     media filter removes [[v ...]] the same way we remove ours, and when it
     gets to an image after us - which is a race, decided by which script's
     frame runs first after Horizon re-renders the gallery - its strip is not
     the alt we wrote. Taken for a rewrite, that threw away the config we had
     just parsed; the re-parse then found no tag (we had stripped it) and the
     preview vanished until a later colour happened to go the other way.
     A strip leaves the wording as it was and none of our tags; Horizon
     putting a fresh alt on the element brings a tag back or different words. */
  function onlyTagsStripped(img) {
    const raw = img.dataset.susuAltRaw;
    if (raw == null) return false;
    const alt = img.getAttribute('alt') || '';
    if (ZONES.some((z) => markerRe(z.marker).test(alt))) return false;
    return altWords(alt) === altWords(raw);
  }

  /* Alt text changed under us - drop every cached zone config on this image. */
  function clearConfigCache(img) {
    ZONES.forEach((z) => img.removeAttribute('data-susu-cfg-' + z.marker));
  }

  function matchesColour(cfg, colour) {
    if (!cfg.for) return true;   // image is colour-agnostic
    if (!colour) return false;   // image is colour-specific, nothing selected
    return String(cfg.for).split(',').some((c) => norm(c) === norm(colour));
  }

  /* ======================= field / zone state ======================= */

  function textToLines(value) {
    let raw = String(value == null ? '' : value).replace(/\r\n?/g, '\n');
    if (SPLIT_ON_PIPE) raw = raw.replace(/\s*\|\s*/g, '\n');
    const lines = raw.split('\n').map((l) => l.replace(/[\x00-\x1f\x7f]/g, '').trim());
    while (lines.length && lines[lines.length - 1] === '') lines.pop();
    while (lines.length && lines[0] === '') lines.shift();
    return lines.slice(0, MAX_LINES);
  }

  function fieldsForZone(zone) {
    const out = [];
    document
      .querySelectorAll('[name="properties[' + zone.property + ']"]')
      .forEach((input) => {
        if (input.disabled) return;
        if (input.type === 'hidden') return;

        const productArea =
          input.closest('product-component, product-form-component, form') || document;
        const picker = productArea.querySelector
          ? productArea.querySelector('variant-picker')
          : null;

        const active = zoneActive(zone, selectedOptionValue(picker, OPTION_NAMES));
        if (active === false) return;   // null = product has no such option, allow

        const lines = textToLines(input.value);
        if (!lines.length) return;

        out.push({
          productArea: productArea,
          colour: selectedOptionValue(picker, COLOUR_OPTIONS),
          lines: lines
        });
      });
    return out;
  }

  /* ===================== show / hide the fields ===================== */

  function updateFieldVisibility() {
    /* List mode: the product declares its personalisation as a field list
       rather than as values of a variant option. The three stock
       product-custom-property blocks sit on the ONE product template, so
       they render on every product; without this they would show on a
       list-mode product AND be forced required by the line below. */
    const listMode = !!document.querySelector('[data-susu-list-mode]');

    ZONES.forEach((zone) => {
      /* The list block owns its own fields - it must not inherit `required`. */
      if (zone.listMode) return;

      document
        .querySelectorAll('[name="properties[' + zone.property + ']"]')
        .forEach((input) => {
          /* [data-susu-field] lets a Custom Liquid block (e.g. a preset
             dropdown) opt into the same show/hide behaviour. */
          const field = input.closest(
            'product-custom-property-component, [data-susu-field]'
          );
          if (!field) return;

          const productArea = field.closest('product-component') || document;
          const picker = productArea.querySelector('variant-picker');
          if (!picker) return;

          const active = zoneActive(zone, selectedOptionValue(picker, OPTION_NAMES));
          /* In list mode a variant-mode zone shows only if an option value
             NAMES it; elsewhere `null` (no such option at all) still means
             "leave visible", which docs/personalisation.md relies on. */
          const show = listMode ? active === true : active !== false;

          field.hidden = !show;
          field.style.display = show ? '' : 'none';
          input.disabled = !show;
          input.required = show;

          if (show) {
            input.setAttribute('aria-required', 'true');
          } else {
            input.removeAttribute('aria-required');

            /* Stop stale text being submitted when the customer switches the
               position off. Both dispatches matter - the preview and any
               other listener react to them. */
            if (input.value !== '') {
              input.value = '';
              input.dispatchEvent(new Event('input', { bubbles: true }));
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
    });
  }

  /* ============================ geometry ============================ */

  /* The rectangle the image pixels actually occupy inside the <img> box,
     honouring object-fit / object-position. Viewport coordinates. */
  function paintedBox(img) {
    const r = img.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh) return null;

    const cs = getComputedStyle(img);
    const bl = parseFloat(cs.borderLeftWidth) || 0;
    const bt = parseFloat(cs.borderTopWidth) || 0;
    const pl = parseFloat(cs.paddingLeft) || 0;
    const pr = parseFloat(cs.paddingRight) || 0;
    const pt = parseFloat(cs.paddingTop) || 0;
    const pb = parseFloat(cs.paddingBottom) || 0;
    const br = parseFloat(cs.borderRightWidth) || 0;
    const bb = parseFloat(cs.borderBottomWidth) || 0;

    const cw = r.width - bl - br - pl - pr;
    const ch = r.height - bt - bb - pt - pb;
    if (cw <= 0 || ch <= 0) return null;

    let fit = cs.objectFit || 'fill';
    if (fit === 'scale-down') fit = (nw <= cw && nh <= ch) ? 'none' : 'contain';

    let w, h;
    if (fit === 'contain') { const s = Math.min(cw / nw, ch / nh); w = nw * s; h = nh * s; }
    else if (fit === 'cover') { const s = Math.max(cw / nw, ch / nh); w = nw * s; h = nh * s; }
    else if (fit === 'none') { w = nw; h = nh; }
    else { w = cw; h = ch; }

    const pos = (cs.objectPosition || '50% 50%').trim().split(/\s+/);
    const ox = resolvePos(pos[0], cw - w, 'x');
    const oy = resolvePos(pos.length > 1 ? pos[1] : '50%', ch - h, 'y');

    return {
      left: r.left + bl + pl + ox,
      top: r.top + bt + pt + oy,
      width: w,
      height: h
    };
  }

  function resolvePos(token, free, axis) {
    if (token == null) return free / 2;
    const t = token.toLowerCase();
    if (t === 'left' && axis === 'x') return 0;
    if (t === 'right' && axis === 'x') return free;
    if (t === 'top' && axis === 'y') return 0;
    if (t === 'bottom' && axis === 'y') return free;
    if (t === 'center' || t === 'centre') return free / 2;
    if (t.slice(-1) === '%') { const p = parseFloat(t); return isFinite(p) ? free * p / 100 : free / 2; }
    const px = parseFloat(t);
    return isFinite(px) ? px : free / 2;
  }

  /* The element the overlay is parented to. Skips <picture>, display:contents
     and inline wrappers, which can't act as a containing block. */
  function pickHost(img) {
    let el = img.parentElement;
    for (let i = 0; el && i < 5; i++) {
      const cs = getComputedStyle(el);
      const usable = cs.display !== 'contents' &&
        cs.display.indexOf('inline') !== 0 &&
        el.tagName !== 'PICTURE' &&
        el.getBoundingClientRect().width > 0;
      if (usable) return el;
      el = el.parentElement;
    }
    return img.parentElement;
  }

  function isVisible(img) {
    if (typeof img.checkVisibility === 'function') {
      if (!img.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true })) return false;
    } else if (!img.offsetParent && getComputedStyle(img).position !== 'fixed') {
      return false;
    }
    /* Off-panel carousel slides: clipped away by a scroll/overflow ancestor. */
    const r = img.getBoundingClientRect();
    let el = img.parentElement;
    for (let i = 0; el && i < 8; i++) {
      const cs = getComputedStyle(el);
      if (cs.overflow !== 'visible' || cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
        const cr = el.getBoundingClientRect();
        const overlapX = Math.min(r.right, cr.right) - Math.max(r.left, cr.left);
        const overlapY = Math.min(r.bottom, cr.bottom) - Math.max(r.top, cr.top);
        if (overlapX < r.width * 0.5 || overlapY < r.height * 0.5) return false;
      }
      el = el.parentElement;
    }
    return true;
  }

  /* ============================= overlay ============================= */

  /* The separator is an escaped NUL because it is the one character that can
     never appear in a marker or in a numeric uid, so the key is unambiguous.
     Written as an escape, not a literal byte, to keep this file pure ASCII. */
  function overlayKey(img, zone) { return zone.marker + '\u0000' + imgUid(img); }

  function prepareHost(host) {
    if (!host.dataset.susuBpHost) {
      host.dataset.susuBpHost = '1';
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    }
    return host;
  }

  function ensureOverlay(img, zone) {
    const key = overlayKey(img, zone);
    const existing = overlays.get(key);
    if (existing && existing.ov.isConnected && existing.ov.parentElement) {
      /* Re-home if the right host has changed. An overlay created while its
         image was momentarily hidden (mid variant switch, before the media
         filter re-showed it) gets parented to a grandparent, because a hidden
         element measures zero and pickHost walks past it. Left alone it would
         stay attached to the wrong element for the life of the page. */
      const host = pickHost(img);
      if (host && existing.ov.parentElement !== host) {
        prepareHost(host).appendChild(existing.ov);
      }
      return existing.ov;
    }

    const host = pickHost(img);
    if (!host) return null;
    prepareHost(host);

    const ov = document.createElement('div');
    ov.className = 'susu-print-overlay';
    ov.setAttribute('data-zone', zone.id);
    ov.setAttribute('data-img', imgUid(img));
    ov.setAttribute('aria-hidden', 'true');
    ov.style.cssText = [
      'position:absolute', 'pointer-events:none', 'overflow:hidden',
      'z-index:2', 'display:none', 'contain:layout paint'
    ].join(';');

    const box = document.createElement('div');
    box.className = 'susu-print-box';
    box.style.cssText = [
      'position:absolute', 'display:flex', 'flex-direction:column',
      'align-items:center', 'justify-content:center', 'text-align:center'
    ].join(';');

    ov.appendChild(box);
    host.appendChild(ov);
    overlays.set(key, { img: img, ov: ov });

    if (ro) { ro.observe(img); ro.observe(host); }
    if (!img.dataset.susuBpLoad) {
      img.dataset.susuBpLoad = '1';
      img.addEventListener('load', scheduleUpdate);
    }
    return ov;
  }

  function paint(img, zone, cfg, lines, scope) {
    const painted = paintedBox(img);
    const ov = ensureOverlay(img, zone);
    if (!ov) return;

    if (!painted || !isVisible(img)) { ov.style.display = 'none'; return; }

    const debug = DEBUG_ON && (!DEBUG_ZONE || DEBUG_ZONE === zone.id);

    const host = ov.parentElement;
    const hr = host.getBoundingClientRect();
    const hcs = getComputedStyle(host);
    const hbl = parseFloat(hcs.borderLeftWidth) || 0;
    const hbt = parseFloat(hcs.borderTopWidth) || 0;

    ov.style.display = 'block';
    ov.style.left = (painted.left - hr.left - hbl + host.scrollLeft) + 'px';
    ov.style.top = (painted.top - hr.top - hbt + host.scrollTop) + 'px';
    ov.style.width = painted.width + 'px';
    ov.style.height = painted.height + 'px';
    ov.style.outline = debug ? '1px dashed rgba(255,0,0,.9)' : 'none';

    const boxW = painted.width * cfg.w / 100;
    const boxH = painted.height * cfg.h / 100;
    const capPx = painted.height * cfg.size / 100;

    const box = ov.firstElementChild;
    box.style.left = cfg.x + '%';
    box.style.top = cfg.y + '%';
    box.style.width = boxW + 'px';
    box.style.height = boxH + 'px';
    box.style.transform = 'translate(-50%, -50%) rotate(' + cfg.rotate + 'deg)';
    box.style.color = cfg.ink;
    box.style.opacity = cfg.opacity;
    box.style.fontFamily = cfg.font || headingFont(scope);
    box.style.fontWeight = cfg.weight;
    box.style.letterSpacing = cfg.spacing + 'em';
    box.style.lineHeight = cfg.lh;
    box.style.textTransform = cfg.case === 'upper' ? 'uppercase'
      : cfg.case === 'lower' ? 'lowercase' : 'none';
    box.style.textShadow = cfg.shadow > 0
      ? '0 ' + (0.02 * capPx).toFixed(2) + 'px ' + (0.08 * capPx).toFixed(2) +
        'px rgba(0,0,0,' + Math.min(1, cfg.shadow) + ')'
      : 'none';
    box.style.background = debug ? 'rgba(255,0,0,.12)' : 'transparent';

    /* rebuild lines only when the text changed */
    const key = lines.join('\n');
    if (box.dataset.text !== key) {
      box.textContent = '';
      lines.forEach((line) => {
        const span = document.createElement('span');
        span.style.cssText = 'white-space:pre;display:block;flex:0 0 auto';
        span.textContent = line === '' ? '\u005cu200b' : line;
        box.appendChild(span);
      });
      box.dataset.text = key;
    }

    /* auto-fit: measure once at a reference size, scale linearly */
    const REF = 100;
    box.style.fontSize = REF + 'px';
    let widest = 0;
    for (const span of box.children) {
      const w = span.getBoundingClientRect().width;
      if (w > widest) widest = w;
    }
    const stack = lines.length * REF * cfg.lh;
    const scale = Math.min(
      widest > 0 ? boxW / widest : 1,
      stack > 0 ? boxH / stack : 1,
      capPx / REF
    );
    box.style.fontSize = Math.max(MIN_FONT_PX, REF * scale) + 'px';
  }

  /* ============================ main pass ============================ */

  function update() {
    updateFieldVisibility();

    const wanted = new Set();

    ZONES.forEach((zone) => {
      fieldsForZone(zone).forEach((field) => {
        const scope = field.productArea === document ? document : field.productArea;
        const pool = scope.querySelectorAll ? scope.querySelectorAll('img') : [];
        const candidates = [];

        pool.forEach((img) => {
          const cfg = configFor(img, zone.marker);
          if (cfg && matchesColour(cfg, field.colour)) candidates.push({ img: img, cfg: cfg });
        });

        /* Product-scoped images found nothing? Fall back to the whole page
           (lightboxes and sticky galleries often live outside the section). */
        if (!candidates.length && scope !== document) {
          document.querySelectorAll('img').forEach((img) => {
            const cfg = configFor(img, zone.marker);
            if (cfg && matchesColour(cfg, field.colour)) candidates.push({ img: img, cfg: cfg });
          });
        }

        candidates.forEach((c) => {
          wanted.add(overlayKey(c.img, zone));
          paint(c.img, zone, c.cfg, field.lines, scope);
        });
      });
    });

    overlays.forEach((entry, key) => {
      if (!wanted.has(key)) {
        if (entry.ov.isConnected) entry.ov.remove();
        overlays.delete(key);
      }
    });

    /* Bin any overlay we no longer own - e.g. one duplicated by a theme or
       app that clones gallery markup. .susu-bp-overlay is the old class name,
       swept up so a page cached from the inline era can't leave one behind. */
    const mine = new Set();
    overlays.forEach((entry) => mine.add(entry.ov));
    document
      .querySelectorAll('.susu-print-overlay, .susu-bp-overlay')
      .forEach((ov) => { if (!mine.has(ov)) ov.remove(); });
  }

  /* ============================== wiring ============================== */

  function isOurField(el) {
    return !!(el && el.name &&
      ZONES.some((z) => el.name === 'properties[' + z.property + ']'));
  }

  /* ============ blank fields must not reach the order ============
     A blank field means "I don't want this position", so it must not arrive
     as an empty line-item property. Horizon's add-to-cart is a FETCH, not a
     navigation: product-form.js handleSubmit() preventDefault()s and posts
     `new FormData(form)`. A real submit event IS still dispatched (that is how
     susu-order-window.js's gate works), and BOTH that and the FormData
     constructor fire a `formdata` event on the form - which bubbles, so one
     document-level listener covers every cart form on the page, including the
     ones Horizon re-renders on a variant change.

     Why not just keep an empty input disabled: a disabled input cannot be
     typed into. The variant-mode fields are only disabled while HIDDEN; a
     visible, optional, empty list field has to stay usable.

     UNVERIFIED: Shopify may already discard blank line-item properties on
     /cart/add. This is correct either way. See docs/personalisation-list.md. */

  function ownedPropertyNames() {
    const names = new Set();
    ZONES.forEach((z) => names.add('properties[' + z.property + ']'));
    /* Capture-only fields declare no marker, so they are not in ZONES. */
    document.querySelectorAll('[data-susu-property]').forEach((el) => {
      const key = el.getAttribute('data-susu-property');
      if (key) names.add('properties[' + key + ']');
    });
    return names;
  }

  function stripEmptyProperties(data) {
    if (!data || typeof data.getAll !== 'function') return;
    ownedPropertyNames().forEach((name) => {
      const values = data.getAll(name);
      if (!values.length) return;
      const kept = values.filter((v) => typeof v === 'string' && v.trim() !== '');
      if (kept.length === values.length) return;   // nothing blank, leave it alone
      data.delete(name);
      kept.forEach((v) => data.append(name, v));
    });
  }

  const isCartForm = (el) =>
    !!(el && el.matches && el.matches('form[action*="/cart/add"]'));

  function start() {
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(scheduleUpdate);
    }

    document.addEventListener('input', (e) => {
      if (isOurField(e.target)) scheduleUpdate();
    });

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('variant-picker') || isOurField(t)) scheduleUpdate();
    });

    document.addEventListener('formdata', (e) => {
      if (isCartForm(e.target)) stripEmptyProperties(e.formData);
    });

    /* Gallery thumbnails / slide changes */
    document.addEventListener('click', (e) => {
      if (e.target && e.target.closest && e.target.closest('img, button, a')) scheduleUpdate();
    }, true);

    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('orientationchange', scheduleUpdate);
    document.addEventListener('shopify:section:load', scheduleUpdate);

    /* Swiping a gallery carousel: element scroll events don't reach window,
       so listen in the capture phase, and only for containers holding a
       previewed image. */
    document.addEventListener('scroll', (e) => {
      const t = e.target;
      if (!t || t.nodeType !== 1 || !t.contains) return;
      for (const entry of overlays.values()) {
        if (t.contains(entry.img)) { scheduleUpdate(); return; }
      }
    }, { capture: true, passive: true });

    /* Transform-based sliders animate rather than scroll: re-check after the
       interaction, plus once more when the slide animation has settled. */
    const settle = () => { scheduleUpdate(); setTimeout(scheduleUpdate, 400); };
    ['pointerup', 'touchend', 'wheel', 'keyup'].forEach((ev) => {
      document.addEventListener(ev, settle, { capture: true, passive: true });
    });

    /* Horizon re-renders the media gallery on variant change */
    const mo = new MutationObserver((records) => {
      let hit = false;
      for (const rec of records) {
        const t = rec.target;
        if (t && t.closest && t.closest('.susu-print-overlay')) continue;
        /* Alt rewritten under us - our cached parse is stale. Our own tag
           strip also lands here, so ignore a value we wrote ourselves. */
        if (rec.type === 'attributes' && rec.attributeName === 'alt' &&
          t && t.tagName === 'IMG' &&
          t.getAttribute('alt') !== t.dataset.susuAltClean) {
          if (onlyTagsStripped(t)) {
            t.dataset.susuAltClean = t.getAttribute('alt');   // still ours, one tag lighter
          } else {
            delete t.dataset.susuAltClean;
            delete t.dataset.susuAltRaw;   // the new alt is the original now
            clearConfigCache(t);
          }
        }
        hit = true;
      }
      if (hit) scheduleUpdate();
    });
    mo.observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['alt', 'src', 'srcset', 'class', 'hidden']
    });

    scheduleUpdate();
    /* Catch late-loading galleries / fonts without polling forever */
    [150, 500, 1200].forEach((ms) => setTimeout(scheduleUpdate, ms));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
